import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

type LabelMap = Record<string, string>;

interface BreadcrumbContextValue {
  labels: LabelMap;
  setLabel: (segment: string, label: string) => void;
  clearLabel: (segment: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<LabelMap>({});

  const setLabel = useCallback((segment: string, label: string) => {
    setLabels((prev) => (prev[segment] === label ? prev : { ...prev, [segment]: label }));
  }, []);

  const clearLabel = useCallback((segment: string) => {
    setLabels((prev) => {
      if (!(segment in prev)) return prev;
      const next = { ...prev };
      delete next[segment];
      return next;
    });
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ labels, setLabel, clearLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

function useBreadcrumbContext(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) {
    return { labels: {}, setLabel: () => {}, clearLabel: () => {} };
  }
  return ctx;
}

export function useBreadcrumbLabels(): LabelMap {
  return useBreadcrumbContext().labels;
}

/**
 * Register a human-readable label for a URL segment (e.g. a UUID `:id`).
 * The label is automatically cleared when the component unmounts or the
 * segment/label changes.
 */
export function useBreadcrumbLabel(segment: string | undefined, label: string | undefined) {
  const { setLabel, clearLabel } = useBreadcrumbContext();
  useEffect(() => {
    if (!segment || !label) return;
    setLabel(segment, label);
    return () => clearLabel(segment);
  }, [segment, label, setLabel, clearLabel]);
}
